import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classNames from "classnames";
import { memo } from "react";
import { getNodeExpanded, treeNodePropsEqual } from "./nodeConfig.js";
import { TreeNodeItem } from "./nodeItem.js";
import { useNodeInteraction } from "./useNodeInteraction.js";
import { useFolderTree } from "../../contexts/folderTree.js";
import { useFolderTreeRuntime } from "../../contexts/folderTreeRuntime.js";
import { useTreeInteraction } from "../../contexts/treeInteraction.js";
import { toId } from "../../types/treeNode.js";
import { isCreatableRootPlacement } from "../../util/applyCreatableDraftPlacement.js";
const TreeNodeRenderer = (nodeProps)=>{
    const { renderItem: configRenderItem, renderIcon: configRenderIcon, onAddClose: configOnAddClose, onSelect: configOnSelect, renameSaveText: configRenameSaveText, renameAddText: configRenameAddText, creatableDraft: contextCreatableDraft, creatableParentId: contextCreatableParentId, getCreatableRootPlacementIds, rootId: contextRootId } = useFolderTree();
    const { onRenameClose: configOnRenameClose, onAddClose: runtimeOnAddClose, onNodePressF2: configOnNodePressF2 } = useFolderTreeRuntime();
    const { getExpandedState, handleNodeExpand, handleCheck: contextHandleCheck, registerSortableUl, renamingNodeId } = useTreeInteraction();
    const { icon, childNodes = [], id, title = "", count = 0, selected, $rename, $busy, $droppable = true, $create: nodeCreate, $visible = true, $_create, searchSelected, attr = {}, checked, indeterminate, expandedState: expandedStateProp, displayChildren = true, renderItem = configRenderItem, renderIcon = configRenderIcon, $checkable, onRenameClose = configOnRenameClose, onCheck: onCheckProp = contextHandleCheck, onAddClose = runtimeOnAddClose ?? configOnAddClose, onSelect = configOnSelect, onNodePressF2 = configOnNodePressF2, onExpand = handleNodeExpand, onUlRef = registerSortableUl, renameSaveText = configRenameSaveText, className: classNameProp, iconActive } = nodeProps;
    const rootPlacementIds = getCreatableRootPlacementIds?.() ?? [
        contextRootId,
        "ALL",
        "all"
    ];
    const isVirtualRootNode = id != null && isCreatableRootPlacement(id, rootPlacementIds);
    const $create = contextCreatableDraft && contextCreatableParentId == id && !isVirtualRootNode ? contextCreatableDraft : nodeCreate;
    const expandedState = expandedStateProp ?? getExpandedState();
    const expanded = getNodeExpanded(id, expandedState);
    const isRenaming = $_create ? !!$rename : id !== undefined && renamingNodeId === id;
    const saveLabel = $_create ? configRenameAddText : renameSaveText;
    const { inputValue, handleRef, handleButtonSave, handleInputKeyDown, handleNodeKeyDown, handleInputChange, handleSelect, handleToggle, handleCheck } = useNodeInteraction({
        isCreateNode: !!$_create,
        isRenaming,
        title,
        searchSelected,
        expanded,
        id,
        icon,
        childNodes,
        classNameProp,
        onAddClose,
        onRenameClose,
        onSelect,
        onExpand,
        onCheck: onCheckProp,
        onNodePressF2
    });
    if (!$visible) {
        return null;
    }
    const nodeAttr = {
        expandedState,
        displayChildren,
        $checkable,
        $rename: isRenaming
    };
    const visibleChildNodes = childNodes.filter(({ $visible: childVisible = true })=>!!childVisible);
    const togglable = !!(displayChildren && visibleChildNodes.length);
    const isExpanded = expanded || !!$create;
    const isActive = $create ? false : $_create ? true : selected;
    const className = classNames("aiot-node", classNameProp, {
        "aiot-active": isActive,
        "aiot-forceEnable": isRenaming,
        "aiot-togglable": togglable,
        "aiot-expanded": expanded,
        "aiot-search-selected": searchSelected,
        "aiot-droppable": $droppable && !$_create,
        "aiot-checkable": $checkable,
        "aiot-checked": checked
    });
    const useIcon = selected ? iconActive || icon : icon;
    const iconNode = renderIcon ? renderIcon(useIcon, {
        id,
        title,
        icon,
        childNodes,
        className: classNameProp
    }) : useIcon;
    const isUlVisible = isExpanded && (togglable || !!$create);
    const isSortable = !!displayChildren && !$_create;
    const refSortable = (ref)=>{
        if (!displayChildren) {
            return;
        }
        if (ref) {
            id !== undefined && onUlRef?.(ref, id);
            return;
        }
        id !== undefined && onUlRef?.(undefined, id);
    };
    const createTreeNode = (node, opts)=>/*#__PURE__*/ _jsx(TreeNodeRenderer, {
            ...node,
            ...nodeAttr,
            ...opts
        }, node.id);
    const renderChildren = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("ul", {
                    className: classNames({
                        "aiot-sortable-one": childNodes.length === 1
                    }),
                    "data-childs-for": id,
                    ref: refSortable,
                    children: [
                        childNodes.map((node)=>renderItem ? renderItem(createTreeNode, TreeNodeMemo, node) : createTreeNode(node)),
                        childNodes.length === 1 && /*#__PURE__*/ _jsx("li", {
                            className: classNames("aiot-sortable-placeholder", {
                                "aiot-sortable": isSortable
                            })
                        }),
                        !!$create && !isVirtualRootNode && togglable && /*#__PURE__*/ _jsx(TreeNodeRenderer, {
                            $_create: true,
                            ...$create
                        })
                    ]
                }),
                !!$create && !togglable && !isVirtualRootNode && /*#__PURE__*/ _jsx("ul", {
                    children: /*#__PURE__*/ _jsx(TreeNodeRenderer, {
                        $_create: true,
                        ...$create
                    })
                })
            ]
        });
    // Outside isUlVisible: leaf nodes are never togglable, so renderChildren() stays unmounted — nesting still needs ul to make it sortable.
    const emptySortableUl = !childNodes.length && isSortable ? /*#__PURE__*/ _jsx("ul", {
        "data-childs-for": id,
        ref: refSortable,
        className: "aiot-sortable-empty"
    }) : undefined;
    return /*#__PURE__*/ _jsx(TreeNodeItem, {
        id: id,
        attr: attr,
        className: className,
        title: title,
        count: count,
        inputValue: inputValue,
        isCreateNode: !!$_create,
        isRenaming: isRenaming,
        isSortable: isSortable,
        isUlVisible: isUlVisible,
        isExpanded: isExpanded,
        togglable: togglable,
        emptySortableUl: emptySortableUl,
        busy: !!$busy,
        checked: !!checked,
        indeterminate: !!indeterminate,
        checkable: !!$checkable,
        saveLabel: saveLabel,
        iconNode: iconNode,
        handleRef: handleRef,
        handleSelect: handleSelect,
        handleToggle: handleToggle,
        handleNodeKeyDown: handleNodeKeyDown,
        handleInputKeyDown: handleInputKeyDown,
        handleInputChange: handleInputChange,
        handleCheck: handleCheck,
        handleButtonSave: handleButtonSave,
        renderChildren: renderChildren
    });
};
const TreeNodeMemo = /*#__PURE__*/ memo(TreeNodeRenderer, treeNodePropsEqual);
const TreeNode = TreeNodeMemo;
export { TreeNode, TreeNodeMemo, toId };
